import React, { type ComponentProps, useCallback } from 'react';

import { useToggleState } from '@sg/garnish';

import { OrderFavoriteLineItem } from '@order/features/orders';

import { ReorderConflictModal } from '../../../../components';
import { useReorderOrderLineItem } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderOrderFavoriteLineItem = (props: ReorderOrderFavoriteLineItemProps) => {
  const {
    favoriteLineItem,
    mostRecentOrder,
    targetRestaurant,
    isTogglingFavoriteState,
    toggleFavoriteLineItem,
  } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'favorite',
    mostRecentOrder,
    targetRestaurant,
    productId: favoriteLineItem.product.id,
    productName: favoriteLineItem.product.name,
    productCost: favoriteLineItem.product.cost,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: hideConflictReviewModal,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(favoriteLineItem.id, true);
  }, [favoriteLineItem.id, reorderLineItem]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <OrderFavoriteLineItem
        favoriteLineItem={favoriteLineItem}
        isReordering={isReordering}
        isTogglingFavoriteState={isTogglingFavoriteState}
        onAddToBagButtonPress={reorderLineItem}
        onToggleFavoriteButtonPress={toggleFavoriteLineItem}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={favoriteLineItem}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderOrderFavoriteLineItemProps = {
  favoriteLineItem: FavoriteLineItem;
  mostRecentOrder: UseReorderOrderLineItemParams['mostRecentOrder'];
  targetRestaurant?: UseReorderOrderLineItemParams['targetRestaurant'];
  isTogglingFavoriteState: boolean;
  toggleFavoriteLineItem: ToggleFavorite;
};

type UseReorderOrderLineItemParams = Parameters<typeof useReorderOrderLineItem>[0];

type FavoriteLineItem = ComponentProps<typeof OrderFavoriteLineItem>['favoriteLineItem'];

type ToggleFavorite = ComponentProps<typeof OrderFavoriteLineItem>['onToggleFavoriteButtonPress'];
